<template>
  <div class="d-flex flex-column" :style="{ gap: '8px' }">
    <v-expansion-panels flat>
      <v-expansion-panel
        v-for="group in activitiesWithStatus"
        :key="`${group.activityType}-${group.isExtra ? 'extra' : 'planned'}`"
        class="rounded mb-1"
      >
        <v-expansion-panel-header class="pa-3">
          <div class="d-flex align-center" :style="{ gap: '8px' }">
            <v-icon x-small :color="group.color">mdi-circle</v-icon>
            <v-chip x-small outlined :color="group.isExtra ? '#AF64FD' : ''">
              {{ group.isExtra ? 'E' : 'P' }}
            </v-chip>
            <span class="font-weight-medium caption text-left">
              {{ group.activityLabel }}
            </span>

            <span v-if="group.isExtra" class="caption text--secondary">
              ({{ group.checkedCount }})
            </span>
            <span v-else class="caption text--secondary">
              ({{ group.checkedCount }}/{{ group.subactivities.length }})
            </span>
          </div>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <v-fade-transition group>
            <div
              v-for="subactivity in group.subactivities"
              :key="subactivity.value"
            >
              <v-checkbox
                v-model="localExecutionActivities"
                :value="subactivity.value"
                :disabled="disabled || readOnly"
                class="ma-0"
                hide-details
                @change="onSubactivityChange(group, subactivity)"
              >
                <template #label>
                  <span>
                    {{ subactivity.label }}
                  </span>
                </template>
              </v-checkbox>
            </div>
          </v-fade-transition>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  const STATUS = Object.freeze({
    PLANNED: 'planned',
    ADDED: 'added',
    MISSED: 'missed',
  });

  const STATUS_COLORS = Object.freeze({
    PLANNED: '#1BA13C',
    ADDED: '#AF64FD',
    MISSED: '#E7090A',
    DEFAULT: '#F5A623',
  });

  export default {
    name: 'EventHistoryActivitySummary',
    props: {
      value: { type: Array, default: () => [] },
      activities: { type: Array, default: () => [] },
      eventActivities: { type: Array, default: () => [] },
      disabled: { type: Boolean, default: false },
      readOnly: { type: Boolean, default: false },
    },
    data: () => ({
      localExecutionActivities: [],
    }),
    computed: {
      eventSubactivityValues() {
        return new Set(
          this.eventActivities.flatMap((a) =>
            a.subactivities.map((s) => s.value)
          )
        );
      },
      activitiesWithStatus() {
        const eventActivityTypes = new Set(
          this.eventActivities.map((a) => a.activity.value)
        );

        return this.activities.map((activity) => {
          const isExtra =
            !eventActivityTypes.has(activity.activity.value) ||
            !this.eventActivities.some(
              (ea) =>
                ea.activity.value === activity.activity.value &&
                ea.subactivities.some((s) =>
                  activity.subactivities.some((as) => as.value === s.value)
                )
            );

          const subactivities = activity.subactivities.map((subactivity) => {
            const isInEvent = this.eventSubactivityValues.has(
              subactivity.value
            );
            const isChecked = this.localExecutionActivities.includes(
              subactivity.value
            );

            const status =
              isInEvent && isChecked
                ? STATUS.PLANNED
                : !isInEvent && isChecked
                ? STATUS.ADDED
                : STATUS.MISSED;

            return {
              value: subactivity.value,
              label: subactivity.label,
              color: STATUS_COLORS[status],
              status,
            };
          });

          const checkedCount = subactivities.filter((s) =>
            this.localExecutionActivities.includes(s.value)
          ).length;

          const color = isExtra
            ? STATUS_COLORS.ADDED
            : checkedCount === subactivities.length
            ? STATUS_COLORS.PLANNED
            : checkedCount === 0
            ? STATUS_COLORS.MISSED
            : STATUS_COLORS.DEFAULT;

          return {
            activityType: activity.activity.value,
            activityLabel: activity.activity.label,
            isExtra,
            subactivities,
            checkedCount,
            color,
          };
        });
      },
    },
    watch: {
      value: {
        immediate: true,
        handler(val) {
          this.localExecutionActivities = [...val];
        },
      },
    },

    methods: {
      onUpdate() {
        this.$emit('input', [...this.localExecutionActivities]);
      },
      onSubactivityChange(group, subactivity) {
        const isChecked = this.localExecutionActivities.includes(
          subactivity.value
        );

        if (!isChecked && group.isExtra) {
          this.$emit('removeExtraSubactivity', {
            activityType: group.activityType,
            subValue: subactivity.value,
          });
        }
        this.onUpdate();
      },

      getActivityLabel(activityType) {
        return (
          this.activities.find((a) => a.activityType === activityType)
            ?.activityLabel ?? activityType
        );
      },
      getSubactivityLabel(activityType, subValue) {
        return (
          this.activities
            .find((a) => a.activityType === activityType)
            ?.subactivities?.find((s) => s.value === subValue)?.label ??
          subValue
        );
      },
    },
  };
</script>

<style scoped></style>
